'use client';

import { useGetMyWarrantiesQuery } from '@/services/warrantyApi';
import { cn, Spinner } from '@holistic/components';
import {
  ChevronRight,
  ShieldCheck,
  ShieldOff,
  X,
} from '@holistic/components/icons';

import type { MyWarrantyResponse } from '@/types/warranty';

type WarrantyPickerModalProps = {
  onSelect: (warrantyId: string, serialNumber?: string) => void;
  onClose: () => void;
};

const WarrantyPickerModal = ({
  onSelect,
  onClose,
}: WarrantyPickerModalProps) => {
  const { data: warranties, isLoading } = useGetMyWarrantiesQuery();

  return (
    <div className="animate-overlay-in fixed inset-0 z-50 flex items-end justify-center bg-gray-900/40 p-0 backdrop-blur-[2px] sm:items-center sm:p-4">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl bg-white shadow-xl sm:rounded-3xl">
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Garantilerim
            </h3>
            <p className="text-xs text-gray-400">
              Talebi bağlamak istediğiniz garantiyi seçin
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Kapat"
            className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto px-5 pb-5">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : !warranties || warranties.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <span className="flex size-12 items-center justify-center rounded-full bg-gray-50 text-gray-300">
                <ShieldOff className="size-5" />
              </span>
              <p className="text-sm text-gray-500">
                Kayıtlı aktif bir garantiniz yok.
              </p>
            </div>
          ) : (
            warranties.map((w) => (
              <WarrantyRow
                key={w.id}
                warranty={w}
                onSelect={() => {
                  onSelect(w.id, w.serialNumber ?? undefined);
                  onClose();
                }}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const WarrantyRow = ({
  warranty: w,
  onSelect,
}: {
  warranty: MyWarrantyResponse;
  onSelect: () => void;
}) => {
  const isEligible = w.isInWarranty;

  return (
    <div
      onClick={isEligible ? onSelect : undefined}
      className={cn(
        'group flex items-center gap-3.5 rounded-2xl border p-3.5 transition-all duration-200',
        isEligible
          ? 'cursor-pointer border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50'
          : 'cursor-not-allowed border-gray-100 bg-gray-50/60'
      )}
    >
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-full',
          isEligible
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-gray-100 text-gray-400'
        )}
      >
        {isEligible ? (
          <ShieldCheck className="size-4.5" />
        ) : (
          <ShieldOff className="size-4.5" />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'truncate text-sm font-semibold',
              isEligible ? 'text-gray-900' : 'text-gray-500'
            )}
          >
            {w.modelName ?? 'Cihaz'}
          </span>
          {isEligible ? (
            <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
              {w.remainingDays} gün
            </span>
          ) : (
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              Süresi doldu
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-gray-400">
          {w.requestTypeName} · {w.referenceNumber}
        </p>
        {w.serialNumber && (
          <p className="mt-0.5 truncate font-mono text-[11px] text-gray-400">
            {w.serialNumber}
          </p>
        )}
      </div>

      {isEligible && (
        <ChevronRight className="size-4 shrink-0 text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-emerald-600" />
      )}
    </div>
  );
};

export default WarrantyPickerModal;
